import cn from 'classnames';

const SingleColumnNutritionTableContentRow = ({
  displayPercent,
  isHeader,
  isLastRow,
  isVitaminInfo,
  percentAsPackaged,
  rowClass,
  title,
  valueAsPackaged,
  addedSugarsRow,
}) => {
  const thtdBaseStyle = 'px-[3px] pb-[2px] pt-[3px] align-baseline leading-none';
  const beforeStyles =
    'before:content-[""] before:absolute before:top-0 before:right-0 before:h-px before:bg-black-100';

  return (
    <tr
      className={cn(rowClass, {
        '!border-t-0': addedSugarsRow,
        'border-b-[12px] border-solid border-black-100': isLastRow,
      })}
    >
      <th
        scope="row"
        colSpan={displayPercent ? '2' : '3'}
        className={cn(thtdBaseStyle, {
          '!pl-3': !isHeader && !isVitaminInfo && !addedSugarsRow,
          'relative !pl-6 before:left-[20px]': addedSugarsRow,
          [beforeStyles]: addedSugarsRow,
          '!pl-[3px]': isVitaminInfo,
        })}
      >
        <span className={cn({ helveticaBlack: isHeader, helvetica: !isHeader })}>{title} </span>
        <span className="helvetica">{valueAsPackaged}</span>
      </th>
      {displayPercent && (
        <td
          className={cn('helveticaBlack text-right', thtdBaseStyle, {
            'relative before:left-0': addedSugarsRow,
            [beforeStyles]: addedSugarsRow,
          })}
        >
          {percentAsPackaged}
        </td>
      )}
    </tr>
  );
};

SingleColumnNutritionTableContentRow.propTypes = {
  displayPercent: PropTypes.bool.isRequired,
  isHeader: PropTypes.bool.isRequired,
  isLastRow: PropTypes.bool,
  isVitaminInfo: PropTypes.bool,
  percentAsPackaged: PropTypes.string.isRequired,
  rowClass: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  valueAsPackaged: PropTypes.string.isRequired,
  addedSugarsRow: PropTypes.bool.isRequired,
};

const ThreeColumnNutritionTableContentRow = ({
  displayPercent,
  isHeader,
  isLastRow,
  percentAsPackaged,
  percentAsPrepared,
  rowClass,
  thStyles,
  title,
  valueAsPackaged,
  valueAsPrepared,
}) => {
  const columnSpan = displayPercent ? 1 : 2;
  const thtdBaseStyle = 'px-[3px] pb-[3px] pt-[4px] align-baseline leading-none';
  const thickBottomBorder =
    'border-b-[12px] relative border-b-transparent after:content-[""] after:block after:absolute after:bottom-[-12px] after:left-1/2 after:-translate-x-1/2 after:bg-black-100 after:h-[12px] after:w-[calc(100%-6px)]';
  const blackBorderStyle = 'border border-solid border-black-100';
  const valueStyle = 'border-r-0 text-left';
  const percentStyle = 'helveticaBlack border-l-0 text-right';

  return (
    <tr
      className={cn(
        rowClass,
        'p-0 [&>*:last-child]:w-1/3 [&>*:last-child]:border-r-0 [&>*:last-child]:pr-0'
      )}
    >
      <th
        scope="row"
        className={cn(thtdBaseStyle, blackBorderStyle, 'w-1/3 border-l-0 text-left', thStyles, {
          'helveticaBlack pl-[0.15em]': isHeader,
          'helvetica pl-[0.75em]': !isHeader,
          [thickBottomBorder]: isLastRow,
        })}
      >
        {title}
      </th>
      <td
        colSpan={columnSpan}
        className={cn(thtdBaseStyle, blackBorderStyle, valueStyle, {
          [thickBottomBorder]: isLastRow,
        })}
      >
        {valueAsPackaged}
      </td>
      {displayPercent && (
        <td
          className={cn(thtdBaseStyle, blackBorderStyle, percentStyle, {
            [thickBottomBorder]: isLastRow,
          })}
        >
          {percentAsPackaged}
        </td>
      )}
      <td
        colSpan={columnSpan}
        className={cn(thtdBaseStyle, blackBorderStyle, valueStyle, {
          [thickBottomBorder]: isLastRow,
        })}
      >
        {valueAsPrepared}
      </td>
      {displayPercent && (
        <td
          className={cn(thtdBaseStyle, blackBorderStyle, percentStyle, {
            [thickBottomBorder]: isLastRow,
          })}
        >
          {percentAsPrepared}
        </td>
      )}
    </tr>
  );
};

ThreeColumnNutritionTableContentRow.propTypes = {
  displayPercent: PropTypes.bool.isRequired,
  isHeader: PropTypes.bool.isRequired,
  isLastRow: PropTypes.bool,
  percentAsPackaged: PropTypes.string.isRequired,
  percentAsPrepared: PropTypes.string.isRequired,
  rowClass: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  thStyles: PropTypes.string,
  valueAsPackaged: PropTypes.string.isRequired,
  valueAsPrepared: PropTypes.string.isRequired,
};

const NutritionTableContentRow = ({
  displayPercent,
  displayPreparedNutritionInfo,
  isHeader,
  isLastRow,
  isVitaminInfo,
  percentAsPackaged,
  percentAsPrepared,
  rowClass,
  thStyles,
  title,
  valueAsPackaged,
  valueAsPrepared,
  addedSugarsRow,
}) => {
  if (displayPreparedNutritionInfo) {
    return (
      <ThreeColumnNutritionTableContentRow
        displayPercent={displayPercent}
        isHeader={isHeader}
        isLastRow={isLastRow}
        percentAsPackaged={percentAsPackaged}
        percentAsPrepared={percentAsPrepared}
        rowClass={rowClass}
        thStyles={thStyles}
        title={title}
        valueAsPackaged={valueAsPackaged}
        valueAsPrepared={valueAsPrepared}
      />
    );
  }

  return (
    <SingleColumnNutritionTableContentRow
      displayPercent={displayPercent}
      isHeader={isHeader}
      isLastRow={isLastRow}
      isVitaminInfo={isVitaminInfo}
      percentAsPackaged={percentAsPackaged}
      percentAsPrepared={percentAsPrepared}
      rowClass={rowClass}
      title={title}
      valueAsPackaged={valueAsPackaged}
      valueAsPrepared={valueAsPrepared}
      addedSugarsRow={addedSugarsRow}
    />
  );
};

NutritionTableContentRow.propTypes = {
  displayPercent: PropTypes.bool.isRequired,
  displayPreparedNutritionInfo: PropTypes.bool.isRequired,
  isHeader: PropTypes.bool.isRequired,
  isLastRow: PropTypes.bool,
  isVitaminInfo: PropTypes.bool,
  percentAsPackaged: PropTypes.string.isRequired,
  percentAsPrepared: PropTypes.string.isRequired,
  rowClass: PropTypes.string.isRequired,
  thStyles: PropTypes.string,
  title: PropTypes.string.isRequired,
  valueAsPackaged: PropTypes.string.isRequired,
  valueAsPrepared: PropTypes.string.isRequired,
  addedSugarsRow: PropTypes.bool.isRequired,
};

export default NutritionTableContentRow;
